import {
  UnmanagedBusinessAutoRegisterCondition,
  UnmanagedBusinessAutoRegisterField,
  UnmanagedBusinessPlaceholderField,
  UnmanagedBusinessAutoRegisterSettingConditionItem,
  UnmanagedBusinessAutoRegisterSettingItem,
} from '@/coreDomainsModule/constants/unmanagedBusinessAutoRegister';
import { IUnmanagedBusiness } from '@/coreDomainsModule/entities/unmanagedBusiness';

const autoFillTargetFields = [
  'supplierName',
  'supplierNumber',
  'businessName',
  'businessNumber',
  'warehouse',
  'warehouseNumber',
  'customerName',
  'customerNumber',
  'salesDestinationName',
  'salesDestinationNumber',
] as const;

const aggregatePlaceholderFieldSet = new Set<UnmanagedBusinessPlaceholderField>([
  'supplierName',
  'supplierNumber',
  'businessName',
  'businessNumber',
  'warehouse',
  'warehouseNumber',
  'package',
]);

const basePlaceholderFieldSet = new Set<UnmanagedBusinessPlaceholderField>([
  'customerName',
  'customerNumber',
  'salesDestinationName',
  'salesDestinationNumber',
]);

const userCustomParameterOptionalFields = [] as const;

const masterFieldsByConditionFieldType = {
  supplier: {
    code: 'supplierNumber',
    name: 'supplierName',
  },
  business: {
    code: 'businessNumber',
    name: 'businessName',
  },
  warehouse: {
    code: 'warehouseNumber',
    name: 'warehouse',
  },
  customer: {
    code: 'customerNumber',
    name: 'customerName',
  },
  salesDestination: {
    code: 'salesDestinationNumber',
    name: 'salesDestinationName',
  },
} as const;
/**
 * ユーザー任意項目へ格納する任意登録項目キー
 */
type UserCustomParameterOptionalField = (typeof userCustomParameterOptionalFields)[number];

/**
 * 未管理取引の自動登録判定や補正を行うサービス
 */
export class UnmanagedBusinessAutoRegisterService {
  /**
   * 有効な自動登録条件が1つ以上存在するかを判定する
   * @param condition - 自動登録条件
   */
  public static hasEffectiveCondition(condition: UnmanagedBusinessAutoRegisterCondition): boolean {
    return condition.settings.some((setting) => this.getEffectiveSettingConditions(setting).length > 0);
  }

  /**
   * 自動登録条件に一致するかを判定する
   * @param business - 判定対象の未管理取引
   * @param condition - 自動登録条件
   */
  public static isMatchedCondition(business: IUnmanagedBusiness, condition: UnmanagedBusinessAutoRegisterCondition): boolean {
    if (condition.enabled === false) {
      return false;
    }

    return condition.settings.some((setting) => this.isMatchedSetting(business, setting));
  }

  /**
   * 一致した設定を取得する
   * @param business - 判定対象の未管理取引
   * @param condition - 自動登録条件
   */
  public static findMatchedSetting(
    business: IUnmanagedBusiness,
    condition: UnmanagedBusinessAutoRegisterCondition
  ): UnmanagedBusinessAutoRegisterSettingItem | undefined {
    if (condition.enabled === false || condition.settings.length === 0) {
      return undefined;
    }

    return condition.settings.find((setting) => this.isMatchedSetting(business, setting));
  }

  /**
   * 設定に含まれる登録項目を未管理取引へ適用する
   * @param business - 変換対象の未管理取引
   * @param setting - 適用する設定
   */
  public static applyRegisterSettingToBusiness(
    business: IUnmanagedBusiness,
    setting: UnmanagedBusinessAutoRegisterSettingItem
  ): IUnmanagedBusiness {
    const copiedBusiness: IUnmanagedBusiness = {
      ...business,
      leadTime: setting.registerContent.leadTime,
      leadTimeUnit: setting.registerContent.leadTimeUnit,
      stdInventoryIndices: {
        標準在庫指数: {
          min: setting.registerContent.stdInventoryIndicesMin,
          max: setting.registerContent.stdInventoryIndicesMax,
          option: {
            unit: setting.registerContent.stdInventoryIndicesUnit,
          },
        },
      },
    };

    for (const conditionItem of setting.conditions) {
      const targetValue = conditionItem.targetValue.trim();
      if (targetValue === '') {
        continue;
      }

      if (conditionItem.targetFieldType === 'package') {
        copiedBusiness.package = targetValue;
        continue;
      }

      const targetFields = masterFieldsByConditionFieldType[conditionItem.targetFieldType];
      const targetName = conditionItem.targetName?.trim() ?? '';
      if (conditionItem.searchType === 'code') {
        copiedBusiness[targetFields.code] = targetValue;
        if (targetName !== '') {
          copiedBusiness[targetFields.name] = targetName;
        }
      } else {
        copiedBusiness[targetFields.name] = targetValue;
      }
    }

    for (const optionalItem of setting.registerContent.optionalItems) {
      const normalizedTargetValue = optionalItem.targetValue.trim();
      if (normalizedTargetValue === '') {
        continue;
      }
      if (this.isUserCustomParameterOptionalField(optionalItem.targetField)) {
        copiedBusiness.userCustomParameters = {
          ...(copiedBusiness.userCustomParameters ?? {}),
          [optionalItem.targetField]: normalizedTargetValue,
        };
        continue;
      }

      if (
        optionalItem.targetField === 'purchaseUnitPrice' ||
        optionalItem.targetField === 'salesUnitPrice' ||
        optionalItem.targetField === 'moq' ||
        optionalItem.targetField === 'orderPackage'
      ) {
        const numericTargetValue = Number(normalizedTargetValue);
        if (Number.isFinite(numericTargetValue)) {
          copiedBusiness[optionalItem.targetField] = numericTargetValue;
        }
        continue;
      }

      copiedBusiness[optionalItem.targetField] = normalizedTargetValue;
    }

    return copiedBusiness;
  }

  /**
   * 自動登録前に必須KEYの空値を補完した未管理取引を返す
   * 必須項目を通常取引の不使用時と同じ値で補完し、補完対象フィールドを記録する
   * @param business - 補完対象の未管理取引
   * @param unconfiguredFields - 読み込み元セルが設定されていない項目
   */
  public static fillBlankFieldWithPlaceholder(
    business: IUnmanagedBusiness,
    unconfiguredFields: readonly UnmanagedBusinessPlaceholderField[] = []
  ): IUnmanagedBusiness {
    const copiedBusiness: IUnmanagedBusiness = { ...business };
    const aggregatePlaceholderFields: UnmanagedBusinessPlaceholderField[] = [];
    const basePlaceholderFields: UnmanagedBusinessPlaceholderField[] = [];
    const unconfiguredFieldSet = new Set(unconfiguredFields);

    /**
     * 補完した項目をプレースホルダー配列へ記録する
     * @param field - 補完対象の項目
     */
    const addPlaceholderField = (field: UnmanagedBusinessPlaceholderField): void => {
      if (aggregatePlaceholderFieldSet.has(field)) {
        aggregatePlaceholderFields.push(field);
      }
      if (basePlaceholderFieldSet.has(field)) {
        basePlaceholderFields.push(field);
      }
    };

    for (const field of autoFillTargetFields) {
      const currentValue = copiedBusiness[field];
      if (currentValue == null || currentValue === '') {
        copiedBusiness[field] = '-';
        if (unconfiguredFieldSet.has(field) === false) {
          addPlaceholderField(field);
        }
      }
    }
    if (copiedBusiness.package == null || copiedBusiness.package === '' || Number.isFinite(Number(copiedBusiness.package)) === false) {
      copiedBusiness.package = '0.01';
      addPlaceholderField('package');
    }

    copiedBusiness.aggregatePlaceholderFields = aggregatePlaceholderFields.length > 0 ? aggregatePlaceholderFields : undefined;
    copiedBusiness.basePlaceholderFields = basePlaceholderFields.length > 0 ? basePlaceholderFields : undefined;

    return copiedBusiness;
  }

  /**
   * 設定1件に一致するかを判定する
   * @param business - 判定対象の未管理取引
   * @param setting - 判定対象の設定
   */
  private static isMatchedSetting(business: IUnmanagedBusiness, setting: UnmanagedBusinessAutoRegisterSettingItem): boolean {
    const effectiveConditions = this.getEffectiveSettingConditions(setting);
    if (effectiveConditions.length === 0) {
      return false;
    }

    return effectiveConditions.every((conditionItem) => {
      if (conditionItem.targetFieldType === 'package') {
        return this.getFieldValue(business, 'package') === conditionItem.targetValue;
      }

      const targetFields = masterFieldsByConditionFieldType[conditionItem.targetFieldType];
      if (conditionItem.searchType === 'name') {
        return this.getFieldValue(business, targetFields.name) === conditionItem.targetValue;
      }

      const businessCode = this.getFieldValue(business, targetFields.code);
      if (businessCode !== '') {
        return businessCode === conditionItem.targetValue;
      }

      const targetName = conditionItem.targetName?.trim() ?? '';
      return targetName !== '' && this.getFieldValue(business, targetFields.name) === targetName;
    });
  }

  /**
   * 空値を除いた有効条件のみを取得する
   * @param setting - 判定対象の設定
   */
  private static getEffectiveSettingConditions(
    setting: UnmanagedBusinessAutoRegisterSettingItem
  ): UnmanagedBusinessAutoRegisterSettingConditionItem[] {
    return setting.conditions
      .map((conditionItem) => ({
        targetFieldType: conditionItem.targetFieldType,
        searchType: conditionItem.searchType,
        targetValue: conditionItem.targetValue.trim(),
        targetName: conditionItem.targetName?.trim(),
      }))
      .filter((conditionItem) => conditionItem.targetValue !== '');
  }

  /**
   * 条件指定された項目の値を取得する
   * @param business - 未管理取引
   * @param targetField - 取得対象の項目
   */
  private static getFieldValue(business: IUnmanagedBusiness, targetField: UnmanagedBusinessAutoRegisterField): string {
    const value = business[targetField];
    return typeof value === 'string' ? value : '';
  }

  /**
   * ユーザー任意項目へ格納する任意登録項目かを判定する
   * @param targetField - 判定対象の登録項目
   */
  private static isUserCustomParameterOptionalField(targetField: string): targetField is UserCustomParameterOptionalField {
    return userCustomParameterOptionalFields.includes(targetField as UserCustomParameterOptionalField);
  }
}
